// src/manager/hooks/useManager.ts — FINAL RTP FULLY INTEGRATED VERSION
// Flexible RTP control (50-150%), real-time sync, manager-specific actions

/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import api, { BASE_URL, fetchCsrfToken } from '../../shared/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast'; // npm install react-hot-toast

const RTP_MIN_PERCENT = 50;
const RTP_MAX_PERCENT = 150;
const RTP_MAX_DECIMAL = RTP_MAX_PERCENT / 100;

// === INTERFACES (unchanged + RTP types) ===
export interface Table {
  id: string;
  name: string;
  location: string | null;
  runtime_profile?: 'pc' | 'casino';
  rtp: number;
  payout_ratio: number;
  created_at: string;
  manager_email?: string;
}

export interface TableData {
  name: string;
  location: string | null;
  password: string;
  runtimeProfile: 'pc' | 'casino';
  rtp: number;         // 50-150% manager range
  payout_ratio: number;
}

export interface UserData {
  name: string;
  login_id?: string;
  password: string;
  initialCredits?: number;
}

export interface RtpData {
  table_id: string;
  rtp: number;         // 50-150
  payout_ratio: number;
}

export interface Cashout {
  id: string;
  user_email: string;
  amount: number;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

export interface ManagerAccount {
  id: string;
  login_id: string;
  name: string;
  credits: number;
  cashout_rate: number;
  distributed_total: number;
  deducted_total: number;
  transfer_count: number;
}

export interface RiskControlTable {
  tableId: string;
  tableName: string;
  currentRtp: number;
  payoutRatio: number;
  recoveryMode: 'slow' | 'normal' | 'fast';
  protectedPaid: number;
  protectedKills: number;
  recoveredPaid: number;
  comfortDebt: number;
  accruedRecovery: number;
  recoveryWindowHours: number;
  maxTakePct: number;
  minPayoutPct: number;
  lastProtectedPayoutAt?: string | null;
  lastRecoveryAt?: string | null;
  live: boolean;
  tableAccounting?: {
    totalBet: number;
    totalPaid: number;
    profit: number;
    observedRtp: number;
    targetRtp: number;
  } | null;
}

export interface RiskControlPayload {
  modes: Record<string, { label?: string; windowHours?: number; maxTakePct?: number; minPayoutPct?: number }>;
  protectedTiers: string[];
  protectedClientKeys: string[];
  tables: RiskControlTable[];
}

let sharedManagerSocket: Socket | null = null;

export const clearManagerSession = () => {
  localStorage.removeItem('managerToken');
  localStorage.removeItem('managerInfo');
  localStorage.removeItem('managerId');
};

export const disconnectManagerSocket = () => {
  sharedManagerSocket?.removeAllListeners();
  sharedManagerSocket?.disconnect();
  sharedManagerSocket = null;
};

// === HOOK ===
export const useManager = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('managerToken');
  const [socket, setSocket] = useState<Socket | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [tables, setTables] = useState<Table[]>([]);
  const [cashouts, setCashouts] = useState<Cashout[]>([]);
  const [account, setAccount] = useState<ManagerAccount | null>(null);
  const [error, setError] = useState<string>('');
  const socketRef = useRef<Socket | null>(null);

  // === SOCKET.IO CONNECTION (manager-specific room join) ===
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!token) return;

    if (sharedManagerSocket) {
      socketRef.current = sharedManagerSocket;
      setSocket(sharedManagerSocket);
      return;
    }

    const managerInfoRaw = localStorage.getItem('managerInfo');
    const managerInfo = managerInfoRaw ? JSON.parse(managerInfoRaw) : null;
    const managerId = localStorage.getItem('managerId') || managerInfo?.id || '';

    const newSocket = io(BASE_URL, {
      withCredentials: true,
      transports: ['polling'],
      upgrade: false,
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    newSocket.on('connect', () => {
      console.log('Manager socket connected');
      if (managerId) {
        newSocket.emit('join-manager', managerId);
      }
    });

    newSocket.on('rtp-updated', (data: { tableId: string; rtp: number }) => {
      const rtp = Number(data.rtp);
      const rtpPercent = rtp > RTP_MAX_DECIMAL ? rtp : rtp * 100;
      toast(`RTP updated for table ${data.tableId}: ${rtpPercent.toFixed(2)}%`);
      fetchTables();
    });

    newSocket.on('jackpot-win', (data: { tableId: string; amount: number }) => {
      toast.success(`Jackpot win on your table ${data.tableId}: $${data.amount}`);
      fetchTables();
    });

    newSocket.on('cashout-updated', () => {
      toast('Cashout request updated');
      fetchCashouts();
    });

    newSocket.on('credits-updated', () => {
      toast('Credits updated');
      fetchUsers();
      fetchAccount().catch(() => {});
    });

    newSocket.on('disconnect', () => {
      console.log('Manager socket disconnected');
      toast.error('Disconnected from server. Reconnecting...');
    });

    sharedManagerSocket = newSocket;
    socketRef.current = newSocket;
    setSocket(newSocket);

    return () => {
      setSocket(socketRef.current ?? sharedManagerSocket);
    };
  }, [token]);

  // === HELPER: CENTRALIZED AUTH ERROR HANDLING ===
  const handleAuthError = useCallback((err: any) => {
    if (err.response?.status === 429) {
      toast.error('Too many requests. Please try again later.');
      setError('Rate limit exceeded');
    } else if (err.response?.status === 401 || err.response?.status === 403) {
      toast.error('Session expired. Please log in again.');
      setError('Session expired');
      clearManagerSession();
      socketRef.current?.removeAllListeners();
      socketRef.current?.disconnect();
      socketRef.current = null;
      setSocket(null);
      disconnectManagerSocket();
      navigate('/manager/login', { replace: true });
    } else {
      const msg = err.response?.data?.error || 'An error occurred';
      toast.error(msg);
      setError(msg);
    }
  }, [navigate]);

  // === TOKEN VALIDATION (centralized) ===
  const validateToken = useCallback(async (): Promise<void> => {
    const token = localStorage.getItem('managerToken');
    if (!token) {
      navigate('/manager/login');
      throw new Error('No manager token found');
    }

    try {
      await api.get('/manager/validate', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setError('');
    } catch (err: any) {
      const message = err.response?.data?.error || 'Invalid or expired token';
      setError(message);
      clearManagerSession();
      socketRef.current?.removeAllListeners();
      socketRef.current?.disconnect();
      socketRef.current = null;
      setSocket(null);
      disconnectManagerSocket();
      navigate('/manager/login', { replace: true });
      throw new Error(message);
    }
  }, [navigate]);

  // === FETCH USERS (manager's users only) ===
  const fetchUsers = useCallback(async (): Promise<any[]> => {
    const token = localStorage.getItem('managerToken');
    if (!token) throw new Error('No token');

    try {
      const res = await api.get('/manager/users', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(res.data);
      setError('');
      return res.data;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  }, [navigate]);

  // === FETCH TABLES (only assigned to manager) ===
  const fetchTables = useCallback(async (): Promise<Table[]> => {
    const token = localStorage.getItem('managerToken');
    if (!token) throw new Error('No token');

    try {
      const res = await api.get('/manager/tables', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const tables = res.data.map((table: any) => {
        const rawRtp = Number(table.current_rtp ?? table.rtp ?? 0);
        return {
          ...table,
          runtime_profile: table.runtime_profile === 'casino' ? 'casino' : 'pc',
          rtp: rawRtp > RTP_MAX_DECIMAL ? rawRtp / 100 : rawRtp,
          payout_ratio: Number(table.payout_ratio ?? 0),
          created_at: table.created_at || new Date().toISOString(),
        };
      });
      setTables(tables);
      setError('');
      return tables;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  }, [navigate]);

  const fetchAccount = useCallback(async (): Promise<ManagerAccount> => {
    const token = localStorage.getItem('managerToken');
    if (!token) throw new Error('No token');

    try {
      const res = await api.get('/manager/account', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAccount(res.data);
      setError('');
      return res.data;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  }, [navigate]);

  // === FETCH CASHOUTS (only from manager's users/tables) ===
  const fetchCashouts = useCallback(async (): Promise<Cashout[]> => {
    const token = localStorage.getItem('managerToken');
    if (!token) throw new Error('No token');

    try {
      const res = await api.get('/manager/cashouts', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCashouts(res.data);
      setError('');
      return res.data;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  }, [navigate]);

  // === APPROVE CASHOUT ===
  const approveCashout = useCallback(async (cashoutId: string): Promise<void> => {
    const token = localStorage.getItem('managerToken');
    const csrfToken = await fetchCsrfToken();

    try {
      await api.post(
        '/manager/approve-cashout',
        { cashout_id: cashoutId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-CSRF-Token': csrfToken,
          },
        }
      );
      toast.success('Cashout approved');
      setError('');
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  }, [navigate]);

  // === REJECT CASHOUT ===
  const rejectCashout = useCallback(async (cashoutId: string): Promise<void> => {
    const token = localStorage.getItem('managerToken');
    const csrfToken = await fetchCsrfToken();

    try {
      await api.post(
        '/manager/reject-cashout',
        { cashout_id: cashoutId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-CSRF-Token': csrfToken,
          },
        }
      );
      toast.success('Cashout rejected');
      setError('');
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  }, [navigate]);

  // === CREATE TABLE (manager can create with RTP 50-150%) ===
  const createTable = useCallback(async (data: TableData): Promise<any> => {
    const token = localStorage.getItem('managerToken');
    const csrfToken = await fetchCsrfToken();

    // Frontend limit for manager
    if (data.rtp < RTP_MIN_PERCENT || data.rtp > RTP_MAX_PERCENT) {
      throw new Error(`Manager RTP must be between ${RTP_MIN_PERCENT}% and ${RTP_MAX_PERCENT}%`);
    }

    try {
      const res = await api.post('/manager/create-table', data, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-CSRF-Token': csrfToken,
        },
      });
      toast.success('Table created successfully');
      setError('');
      return res.data;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  }, [navigate]);

  // === REGISTER USER (manager creates user) ===
  const registerUser = useCallback(async (data: UserData): Promise<any> => {
    const token = localStorage.getItem('managerToken');
    const csrfToken = await fetchCsrfToken();

    try {
      const res = await api.post('/manager/register-user', data, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-CSRF-Token': csrfToken,
        },
      });
      toast.success('User registered successfully');
      setError('');
      return res.data;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  }, [navigate]);

  // === SET CASHOUT RATE ===
  const setCashoutRate = useCallback(async (cashout_rate: number): Promise<any> => {
    const token = localStorage.getItem('managerToken');
    const csrfToken = await fetchCsrfToken();

    try {
      const res = await api.post(
        '/manager/set-cashout-rate',
        { cashout_rate },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-CSRF-Token': csrfToken,
          },
        }
      );
      toast.success('Cashout rate updated');
      setError('');
      return res.data;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  }, [navigate]);

  // === SET TABLE RTP (manager range 50-150%) ===
  const setTableRtp = useCallback(async (data: RtpData): Promise<any> => {
    const token = localStorage.getItem('managerToken');
    const csrfToken = await fetchCsrfToken();

    // Frontend validation
    if (data.rtp < RTP_MIN_PERCENT || data.rtp > RTP_MAX_PERCENT) {
      throw new Error(`RTP must be between ${RTP_MIN_PERCENT}% and ${RTP_MAX_PERCENT}% for managers`);
    }

    try {
      const res = await api.post(`/manager/tables/${encodeURIComponent(data.table_id)}/rtp`, { rtp: data.rtp }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-CSRF-Token': csrfToken,
        },
      });
      toast.success(`RTP set to ${data.rtp}% for table ${data.table_id}`);
      setError('');
      return res.data;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  }, [navigate]);

  // === SUSPEND USER ===
  const suspendUser = useCallback(async (user_identifier: string, suspend: boolean): Promise<any> => {
    const token = localStorage.getItem('managerToken');
    const csrfToken = await fetchCsrfToken();

    try {
      const res = await api.post(
        '/manager/suspend-user',
        { user_identifier, suspend },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-CSRF-Token': csrfToken,
          },
        }
      );
      toast.success(`User ${suspend ? 'suspended' : 'unsuspended'}`);
      setError('');
      return res.data;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  }, [navigate]);

  const distributeCredits = useCallback(async ({
    user_identifier,
    user_email,
    amount,
  }: {
    user_identifier?: string;
    user_email?: string;
    amount: number;
  }): Promise<any> => {
    const token = localStorage.getItem('managerToken');
    const csrfToken = await fetchCsrfToken();
    const identifier = user_identifier || user_email;

    try {
      const res = await api.post(
        '/manager/distribute-credits',
        { user_identifier: identifier, amount },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-CSRF-Token': csrfToken,
          },
        }
      );
      await Promise.all([fetchUsers(), fetchAccount()]);
      setError('');
      return res.data;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  }, [fetchAccount, fetchUsers, navigate]);

  const fetchCreditTransactions = useCallback(async (): Promise<any[]> => {
    const token = localStorage.getItem('managerToken');
    if (!token) throw new Error('No token');

    try {
      const res = await api.get('/manager/credit-transactions', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setError('');
      return res.data;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  }, [navigate]);

  const fetchRiskControl = useCallback(async (): Promise<RiskControlPayload> => {
    const token = localStorage.getItem('managerToken');
    if (!token) throw new Error('No token');

    try {
      const res = await api.get('/manager/risk-control', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setError('');
      return res.data;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  }, [navigate]);

  const setTableRiskRecoveryMode = useCallback(async (tableId: string, recoveryMode: 'slow' | 'normal' | 'fast'): Promise<RiskControlTable> => {
    const token = localStorage.getItem('managerToken');
    const csrfToken = await fetchCsrfToken();

    try {
      const res = await api.post(
        `/manager/tables/${encodeURIComponent(tableId)}/risk-control`,
        { recoveryMode },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-CSRF-Token': csrfToken,
          },
        }
      );
      setError('');
      return res.data.risk;
    } catch (err: any) {
      handleAuthError(err);
      throw err;
    }
  }, [navigate]);

  // === RETURNED HOOK VALUES ===
  return {
    token,
    socket,
    users,
    tables,
    cashouts,
    account,
    error,
    setError,
    validateToken,
    fetchUsers,
    fetchTables,
    fetchCashouts,
    fetchAccount,
    fetchCreditTransactions,
    fetchRiskControl,
    approveCashout,
    rejectCashout,
    createTable,
    registerUser,
    setCashoutRate,
    setTableRtp,
    suspendUser,
    distributeCredits,
    setTableRiskRecoveryMode,
    fetchCsrfToken,
    disconnectManagerSocket,
    clearManagerSession,
  };
};

export default useManager;
